import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Calendar,
  TrendingUp,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  X,
  FileText,
  ChevronLeft,
  AlertCircle,
  Target,
  Archive,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Upload
} from "lucide-react";
import BulkUploadModal from "../../../components/common/BulkUploadModal";
import ExportModal from "../../../components/common/ExportModal";
import SelectedExportFormatBar from "../../../components/common/SelectedExportFormatBar";
import PaginationControls from "../../../components/common/Pagination/PaginationControls";
import DateRangeFilter from "../../../components/common/DateRangeFilter";

import Button from "../../../components/common/Button/Button";
import PermissionGate from "../../../components/common/PermissionGate/PermissionGate";
import { toast } from "../../../utils/toast";
import WarningPopup from "../../../components/WarningPopup";
import { usePerformanceCycles } from '../../../hooks/usePerformanceCycles';
import useUrlTableState from "../../../hooks/useUrlTableState";
import { formatDate } from "../../../utils/stringUtils";
import { INVALID_CHARS_REGEX, VALID_TEXT_REGEX } from "../../../utils/formValidation";

const PerformanceCycles = ({ initialMode, onClose, isDrawer, cycleId }) => {
  const navigate = useNavigate();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    status: 'all',
    year: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: tableFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateTableFilter,
    updatePage: updateTablePage,
    updateLimit: updateTableLimit,
    resetFilters: resetAllFilters
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;
  const debouncedSearch = tableFilters.search || '';

  const filterStatus = tableFilters.status || 'all';
  const setFilterStatus = (status) => updateTableFilter({ status });

  const filterYear = tableFilters.year || 'all';
  const setFilterYear = (year) => updateTableFilter({ year });

  const filterStartDate = tableFilters.startDate || '';
  const setFilterStartDate = (startDate) => updateTableFilter({ startDate });
  const filterEndDate = tableFilters.endDate || '';
  const setFilterEndDate = (endDate) => updateTableFilter({ endDate });

  const page = tableFilters.page;
  const setPage = (page) => updateTablePage(page);

  const limit = tableFilters.limit;
  const setLimit = (limit) => updateTableLimit(limit);

  // Handle key press for text inputs - allow approved symbols
  const handleTextKeyPress = (e) => {
    // No blocking - sanitization is done at onChange level
  };
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCycle, setSelectedCycle] = useState(null);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [cycleToDelete, setCycleToDelete] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  useEffect(() => {
    if (initialMode === 'create') {
      setShowCreateModal(true);
    }
  }, [initialMode]);

  const cycleFilters = useMemo(
    () => ({
      status: filterStatus !== "all" ? filterStatus : undefined,
      year: filterYear !== "all" ? filterYear : undefined,
      createdAtStart: filterStartDate || undefined,
      createdAtEnd: filterEndDate || undefined,
      q: debouncedSearch || undefined,
      page,
      limit,
    }),
    [filterStatus, filterYear, filterStartDate, filterEndDate, debouncedSearch, page, limit]
  );

  const {
    data: cyclesResponse,
    isLoading,
    isError,
    error,
    refetch,
    createCycle,
    deleteCycle,
    launchCycle,
    updateCycle,
  } = usePerformanceCycles(cycleFilters);

  // Transform API data to match frontend format
  // Handle different response formats and pagination
  let cyclesData = [];
  let paginationData = null;

  // Debug logging
  console.log("🔍 Debug - cyclesResponse:", cyclesResponse);
  console.log("🔍 Debug - isLoading:", isLoading);
  console.log("🔍 Debug - isError:", isError);
  console.log("🔍 Debug - error:", error);

  if (cyclesResponse?.data?.cycles) {
    // New API response structure with pagination
    cyclesData = cyclesResponse.data.cycles;
    paginationData = cyclesResponse.data.pagination;
    console.log(
      "🔍 Debug - Using new API structure, cycles count:",
      cyclesData.length
    );
  } else if (cyclesResponse?.cycles && Array.isArray(cyclesResponse.cycles)) {
    // Direct cycles array in response
    cyclesData = cyclesResponse.cycles;
    console.log(
      "🔍 Debug - Using direct cycles structure, cycles count:",
      cyclesData.length
    );
  } else if (cyclesResponse?.data && Array.isArray(cyclesResponse.data)) {
    // Direct array in data
    cyclesData = cyclesResponse.data;
    console.log(
      "🔍 Debug - Using direct array structure, cycles count:",
      cyclesData.length
    );
  } else if (Array.isArray(cyclesResponse)) {
    // Direct array response (this is what our hook returns)
    cyclesData = cyclesResponse;
    console.log(
      "🔍 Debug - Using array structure, cycles count:",
      cyclesData.length
    );
  } else {
    console.log("🔍 Debug - No valid API data found, using empty array");
    console.log("🔍 Debug - cyclesResponse structure:", cyclesResponse);
  }

  // Use only API data - no fallback to mock data
  const dataToUse = cyclesData;
  console.log("🔍 Debug - Final dataToUse length:", dataToUse.length);
  console.log("🔍 Debug - dataToUse:", dataToUse);

  const transformedCycles = dataToUse.map((cycle) => {
    console.log("🔍 Debug - Transforming cycle:", cycle);
    return {
      id: cycle.id,
      name: cycle.cycleName || cycle.name,
      description: cycle.description || "",
      startDateRaw: cycle.startDate,
      endDateRaw: cycle.endDate,
      period: (() => {
        if (!cycle.startDate || !cycle.endDate) return 'N/A';
        try {
          const startFormatted = formatDate(cycle.startDate, { includeTime: false });
          const endFormatted = formatDate(cycle.endDate, { includeTime: false });
          // Extract month and year from formatted dates (format: "2 Jan, 2025")
          const startParts = startFormatted.split(' ');
          const endParts = endFormatted.split(' ');
          if (startParts.length >= 3 && endParts.length >= 3) {
            return `${startParts[1]} ${startParts[2]} - ${endParts[1]} ${endParts[2]}`;
          }
          return `${startFormatted} - ${endFormatted}`;
        } catch (error) {
          return 'N/A';
        }
      })(),
      year: new Date(cycle.startDate).getFullYear(),
      status: cycle.status,
      population: cycle.metrics?.totalEmployees || cycle.totalEmployees || 0,
      completion:
        cycle.metrics?.completionPercentage || cycle.completionPercentage || 0,
      dueDate: cycle.endDate,
      manager: cycle.manager || "System",
      stages: cycle.stages?.map((stage) => stage.name) || [
        "Self Review",
        "Manager Review",
        "Calibration",
        "Final Rating",
      ],
      createdAt: cycle.createdAt,
      launchedAt: cycle.launchedAt,
      frozenAt: cycle.frozenAt,
      closedAt: cycle.closedAt,
      archived: cycle.isArchived || false,
      averageRating: cycle.metrics?.averageRating || cycle.averageRating || 0,
      // Additional fields from new API
      duration: cycle.duration,
      daysRemaining: cycle.daysRemaining,
      isActive: cycle.isActive,
      isOverdue: cycle.isOverdue,
    };
  });

  console.log("🔍 Debug - transformedCycles length:", transformedCycles.length);
  console.log("🔍 Debug - transformedCycles:", transformedCycles);

  // State for create cycle form
  const [newCycle, setNewCycle] = useState({
    name: "",
    description: "",
    startDate: "",
    endDate: "",
    status: "draft",
  });

  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    let error = undefined;
    switch (name) {
      case 'name':
        if (!value?.trim()) return "Please enter a valid name";
        if (value.length > 100) return "Name must be ≤ 100 characters";
        if (!VALID_TEXT_REGEX.test(value)) return "Name contains invalid characters";
        if (value.trim().length < 3) return "Please enter a valid name (min 3 chars)";
        break;
      case 'description':
        if (!value?.trim()) return "Please enter a valid description";
        if (value.length > 500) return "Description must be ≤ 500 characters";
        if (!VALID_TEXT_REGEX.test(value)) return "Description contains invalid characters";
        if (value.trim().length < 5) return "Please enter a valid description (min 5 chars)";
        break;
      case 'startDate':
        if (!value) return "Start date is required";
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (new Date(value) < today) return "Start date cannot be in the past";
        break;
      case 'endDate':
        if (!value) return "End date is required";
        if (newCycle.startDate && new Date(value) < new Date(newCycle.startDate)) return "End date must be after start date";
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setFormErrors((prev) => ({ ...prev, [name]: error }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "launched":
        return "bg-brand/10 text-brand";
      case "calibrating":
        return "bg-yellow-100 text-yellow-800";
      case "frozen":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "draft":
        return <Clock className="h-4 w-4" />;
      case "launched":
        return <Target className="h-4 w-4" />;
      case "calibrating":
        return <BarChart3 className="h-4 w-4" />;
      case "frozen":
        return <CheckCircle className="h-4 w-4" />;
      case "closed":
        return <Archive className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  const getCyclePeriodDisplay = (cycle) => {
    if (!cycle) return "N/A";
    const start = cycle.startDateRaw || cycle.startDate || cycle.start_date;
    const end = cycle.endDateRaw || cycle.endDate || cycle.end_date;
    try {
      if (start) {
        const startFormatted = formatDate(start, { includeTime: false });
        if (end) {
          const endFormatted = formatDate(end, { includeTime: false });
          return `${startFormatted} - ${endFormatted}`;
        }
        return startFormatted;
      }
      if (cycle.period && cycle.period !== "N/A") {
        return cycle.period;
      }
    } catch (err) {
      console.error("Failed to format cycle period:", err);
    }
    return "N/A";
  };

  // Helper: schedule default stages between start/end
  const scheduleDefaultStages = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return [];
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const totalMs = end.getTime() - start.getTime();
    if (totalMs <= 0) return [];
    const labels = [
      "goal_setting",
      "self_review",
      "manager_review",
      "consolidation",
      "calibration",
      "finalization",
    ];
    const step = Math.floor(totalMs / (labels.length + 1));
    const endMinus1 = new Date(end.getTime() - 24 * 60 * 60 * 1000);
    return labels.map((name, index) => {
      let due = new Date(start.getTime() + step * (index + 1));
      const stageStart = new Date(start.getTime() + step * index);
      if (due > endMinus1) due = endMinus1;
      if (stageStart >= due) {
        due = new Date(stageStart.getTime() + 24 * 60 * 60 * 1000);
        if (due > endMinus1) due = endMinus1;
      }
      return {
        name,
        order: index + 1,
        isRequired: true,
        isCompleted: false,
        startDate: stageStart,
        dueDate: due,
        description: `${name.replace(/_/g, ' ')} phase`,
      };
    });
  };

  const handleCreateCycle = async (e) => {
    e.preventDefault();
    // Basic validations
    const errors = {};
    if (!newCycle.name?.trim()) {
      errors.name = "Please enter a valid name";
    } else if (newCycle.name.length > 100) {
      errors.name = "Name must be ≤ 100 characters";
    } else if (!VALID_TEXT_REGEX.test(newCycle.name)) {
      errors.name = "Name contains invalid characters";
    } else if (newCycle.name.trim().length < 3) {
      errors.name = "Please enter a valid name (min 3 chars)";
    }
    if (!newCycle.description?.trim()) {
      errors.description = "Please enter a valid description";
    } else if (newCycle.description.length > 500) {
      errors.description = "Description must be ≤ 500 characters";
    } else if (!VALID_TEXT_REGEX.test(newCycle.description)) {
      errors.description = "Description contains invalid characters";
    } else if (newCycle.description.trim().length < 5) {
      errors.description = "Please enter a valid description (min 5 chars)";
    }
    if (!newCycle.startDate) {
      errors.startDate = "Start date is required";
    }
    if (!newCycle.endDate) {
      errors.endDate = "End date is required";
    }
    if (newCycle.startDate && newCycle.endDate) {
      const s = new Date(newCycle.startDate);
      const eDate = new Date(newCycle.endDate);
      if (s > eDate) {
        errors.endDate = "End date must be after start date";
      }
      // Start date must not be in the past (date-only)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (s < today) {
        errors.startDate = "Start date cannot be in the past";
      }
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    // Check for required fields explicitly
    if (!newCycle.name?.trim() || !newCycle.description?.trim() || !newCycle.startDate || !newCycle.endDate) {
      toast.error('Please fill all required fields.');
      return;
    }

    try {
      // Prepare data for API
      const cycleData = {
        cycleName: newCycle.name,
        startDate: newCycle.startDate,
        endDate: newCycle.endDate,
        description: newCycle.description,
        status: newCycle.status || "draft",
        // Create with scheduled stages so pre‑launch checks pass without edit
        stages: scheduleDefaultStages(newCycle.startDate, newCycle.endDate),
      };

      console.log("🔍 Debug - Creating cycle with data:", cycleData);

      // Use React Query mutation
      const result = await createCycle(cycleData);
      console.log("🔍 Debug - Create cycle result:", result);

      // Reset form
      setNewCycle({
        name: "",
        description: "",
        startDate: "",
        endDate: "",
        status: "draft",
      });

      setShowCreateModal(false);
      toast.success("Performance cycle created successfully!");
      if (isDrawer && onClose) onClose();

      // Manual refetch to ensure fresh data
      refetch();
    } catch (error) {
      console.error("🔍 Debug - Error creating cycle:", error);
      console.error(
        "🔍 Debug - Error details:",
        error.response?.data || error.message
      );
      toast.error(
        `Failed to create performance cycle: ${error.message || "Unknown error"
        }`
      );
    }
  };

  const handleViewCycle = (cycleId) => {
    navigate(`/performance_cycle_detail/${cycleId}`);
  };

  const handleEditCycle = (cycleId) => {
    navigate(`/performance_cycle_detail/${cycleId}/edit`);
  };

  const handleUpdateStats = async (cycleId, statsData) => {
    try {
      // await updateStatsMutation.mutateAsync({ cycleId, statsData });
      console.log("Update stats functionality not implemented yet");
      toast.success("Cycle statistics updated successfully!");
    } catch (error) {
      console.error("Error updating stats:", error);
      toast.error("Failed to update cycle statistics.");
    }
  };

  const handleDeleteCycle = (cycleId) => {
    console.log("🗑️ Delete cycle clicked for ID:", cycleId);
    const cycle = transformedCycles.find((cycle) => cycle.id === cycleId);
    console.log("🗑️ Found cycle to delete:", cycle);
    setIsBulkDelete(false);
    setCycleToDelete(cycle);
    setShowDeleteWarning(true);
    console.log("🗑️ Set showDeleteWarning to true");
  };

  const handleBulkDelete = () => {
    if (selectedCyclesMap.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteWarning(true);
  };

  const confirmDeleteCycle = async () => {
    if (!cycleToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedCyclesMap.keys());
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            const cycle = selectedCyclesMap.get(id);
            // Only allow deleting draft or closed cycles as per individual delete logic
            if (cycle.status === "draft" || cycle.status === "closed") {
              await deleteCycle(id);
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            failCount++;
            console.error(`Error deleting cycle ${id}:`, error);
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} performance cycle(s) deleted successfully`);
          setSelectedCyclesMap(new Map());
        }
        if (failCount > 0) {
          toast.error(`${failCount} cycle(s) could not be deleted (ensure they are in draft or closed status)`);
        }
      } else {
        await deleteCycle(cycleToDelete.id);
        toast.success("Performance cycle deleted successfully");
      }
      setShowDeleteWarning(false);
      setCycleToDelete(null);
      setIsBulkDelete(false);
      refetch(); // Refresh the list after deletion
    } catch (error) {
      console.error("Error deleting cycle:", error);
      toast.error("Failed to delete performance cycle");
    }
  };

  const cancelDeleteCycle = () => {
    setShowDeleteWarning(false);
    setCycleToDelete(null);
    setIsBulkDelete(false);
  };


  const filteredCycles = transformedCycles;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const sortedCycles = useMemo(() => {
    if (!filteredCycles) return [];
    if (!sortField) return filteredCycles;

    return [...filteredCycles].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'year':
          // Sort by year, then start date
          aValue = a.year || 0;
          bValue = b.year || 0;
          if (aValue === bValue) {
            aValue = new Date(a.startDateRaw || 0).getTime();
            bValue = new Date(b.startDateRaw || 0).getTime();
          }
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'dueDate':
          aValue = new Date(a.dueDate || 0).getTime();
          bValue = new Date(b.dueDate || 0).getTime();
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || 0).getTime();
          bValue = new Date(b.createdAt || 0).getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredCycles, sortField, sortOrder]);

  // --- Row Selection & Export Logic ---

  const [selectedCyclesMap, setSelectedCyclesMap] = useState(new Map()); // Use Map to store full cycle objects
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const selectedExportWrapRef = useRef(null);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleSelectAll = (e) => {
    const newMap = new Map(selectedCyclesMap);
    if (e.target.checked) {
      filteredCycles.forEach(cycle => {
        newMap.set(cycle.id, cycle);
      });
    } else {
      filteredCycles.forEach(cycle => {
        newMap.delete(cycle.id);
      });
    }
    setSelectedCyclesMap(newMap);
  };

  const handleSelectRow = (cycle) => {
    const newMap = new Map(selectedCyclesMap);
    if (newMap.has(cycle.id)) {
      newMap.delete(cycle.id);
    } else {
      newMap.set(cycle.id, cycle);
    }
    setSelectedCyclesMap(newMap);
  };

  // --- Backend-driven Export Logic ---

  // Base export filters for backend API
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      q: debouncedSearch || undefined,
      status: filterStatus !== 'all' ? filterStatus : undefined,
      year: filterYear !== 'all' ? filterYear : undefined,
      createdAtStart: filterStartDate || undefined,
      createdAtEnd: filterEndDate || undefined,
    };
    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });
    return apiFilters;
  }, [debouncedSearch, filterStatus, filterYear, filterStartDate, filterEndDate]);

  // Get current page cycle IDs
  const currentPageCycleIds = useMemo(
    () =>
      filteredCycles
        .map((cycle) => {
          const raw = cycle?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [filteredCycles]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageCycleIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'performance-cycles',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? limit,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `performance-cycles-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, page, limit, currentPageCycleIds]
  );

  // Handle export for selected cycles with format selection
  const handleCyclesSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedCyclesMap.keys()).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected cycles to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedCyclesMap, requestBackendExport]
  );

  // Handle scoped export from modal (all, filtered, currentPage, dateRange)
  const handleCyclesScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              createdAtStart: startDate,
              createdAtEnd: endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageCycleIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [baseExportFilters, requestBackendExport, limit, currentPageCycleIds]
  );

  const handleExportClick = () => {
    if (selectedCyclesMap.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  console.log("🔍 Debug - filteredCycles length:", filteredCycles.length);
  console.log("🔍 Debug - filteredCycles:", filteredCycles);

  // Error state
  if (isError) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Failed to load performance cycles
          </h3>
          <p className="text-red-700 mb-4">
            {error?.message || "An error occurred while loading data"}
          </p>
          <Button
            variant="primary"
            onClick={() => refetch()}
            className="bg-red-600 hover:bg-red-700"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!isDrawer && (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Performance Cycles
              </h1>
              <p className="text-gray-600">
                Manage performance review cycles and configurations
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {selectedCyclesMap.size > 0 && (
                <PermissionGate permission="performance" action="delete">
                  <Button
                    variant="primary"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={handleBulkDelete}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Delete ({selectedCyclesMap.size})
                  </Button>
                </PermissionGate>
              )}
              <PermissionGate permission="performance" action="read">
                <div className="relative" ref={selectedExportWrapRef}>
                  <Button
                    variant="outline"
                    icon={exportLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" /> : <Download className="h-4 w-4" />}
                    onClick={handleExportClick}
                    disabled={exportLoading}
                    className={`flex-shrink-0 ${selectedCyclesMap.size > 0 ? 'bg-blue-50 border-blue-200 text-[#0F2942]' : ''}`}
                    aria-label={
                      selectedCyclesMap.size > 0
                        ? `Export ${selectedCyclesMap.size} selected cycles`
                        : 'Export cycles'
                    }
                  >
                    {selectedCyclesMap.size > 0 ? `Export Selected (${selectedCyclesMap.size})` : "Export"}
                  </Button>
                  <SelectedExportFormatBar
                    show={selectedCyclesMap.size > 0 && showInlineSelectedFormats}
                    disabled={exportLoading}
                    onSelect={handleCyclesSelectedFormatExport}
                  />
                </div>
              </PermissionGate>
              <PermissionGate permission="performance" action="create">
                <Button
                  variant="outline"
                  icon={<Upload className="h-4 w-4" />}
                  onClick={() => setShowBulkUpload(true)}
                  className="bg-brand hover:bg-brand-dark text-white hover:text-white border-transparent"
                >
                  Bulk Upload
                </Button>
              </PermissionGate>
              <PermissionGate permission="performance" action="create">
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={() => setShowCreateModal(true)}
                  className="btn-create"
                >
                  Create Cycle
                </Button>
              </PermissionGate>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex flex-wrap items-end gap-4 w-full">
              <div className="relative flex-[2] min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search cycles..."
                  value={searchTerm}
                  onChange={(e) => {
                    // Allow alphanumeric, spaces, and approved symbols
                    const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                    setSearchTerm(value);
                  }}
                  maxLength={100}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      const input = document.querySelector('input[placeholder="Search cycles..."]');
                      if (input) input.focus();
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div className="flex-shrink-0">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="launched">Launched</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div className="flex-shrink-0">
                <select
                  value={filterYear}
                  onChange={(e) => setFilterYear(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                >
                  <option value="all">All Years</option>
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const years = [];
                    // Add 2 future years
                    for (let i = 2; i >= 1; i--) {
                      years.push(currentYear + i);
                    }
                    // Add current year
                    years.push(currentYear);
                    // Add 3 past years
                    for (let i = 1; i <= 3; i++) {
                      years.push(currentYear - i);
                    }
                    return years.map(year => (
                      <option key={year} value={year.toString()}>{year}</option>
                    ));
                  })()}
                </select>
              </div>
              <div className="flex-shrink-0">
                <DateRangeFilter
                  startDate={filterStartDate}
                  endDate={filterEndDate}
                  onChange={({ startDate, endDate }) => {
                    updateTableFilter({ startDate, endDate });
                  }}
                  inputClassName="w-32 px-2 py-2 border border-gray-300 rounded-lg text-sm outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                />
              </div>
              {(searchTerm || filterStatus !== 'all' || filterYear !== 'all' || filterStartDate || filterEndDate) && (
                <div className="relative flex-shrink-0">
                  <Button
                    variant="outline"
                    icon={<X className="h-4 w-4" />}
                    onClick={resetAllFilters}
                  >
                    Clear
                  </Button>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Create Cycle Modal */}
      {
        showCreateModal && (
          isDrawer ? (
            <div className="bg-white">
              <form onSubmit={handleCreateCycle} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={newCycle.name}
                    onChange={(e) => {
                      const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                      setNewCycle({ ...newCycle, name: value });
                      if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                    }}
                    onBlur={handleBlur}
                    onKeyPress={handleTextKeyPress}
                    maxLength={100}
                    className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.name && formErrors.name ? "border-red-500" : "border-gray-300"}`}
                    required
                  />
                  <div className="flex justify-between items-center mt-1">
                    {touched.name && formErrors.name && (
                      <p className="text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {formErrors.name}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {newCycle.name.length}/100 characters
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={newCycle.description}
                    onChange={(e) => {
                      const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 500);
                      setNewCycle({ ...newCycle, description: value });
                      if (formErrors.description) setFormErrors({ ...formErrors, description: undefined });
                    }}
                    onBlur={handleBlur}
                    onKeyPress={handleTextKeyPress}
                    maxLength={500}
                    className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.description && formErrors.description ? "border-red-500" : "border-gray-300"}`}
                    required
                    rows={6}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {touched.description && formErrors.description && (
                      <p className="text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {formErrors.description}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {newCycle.description.length}/500 characters
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="startDate"
                      value={newCycle.startDate}
                      onChange={(e) => {
                        setNewCycle({ ...newCycle, startDate: e.target.value });
                        if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: undefined });
                      }}
                      onBlur={handleBlur}
                      onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                      className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.startDate && formErrors.startDate ? "border-red-500" : "border-gray-300"}`}
                      min={(new Date()).toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  {touched.startDate && formErrors.startDate && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.startDate}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="endDate"
                      value={newCycle.endDate}
                      onChange={(e) => {
                        setNewCycle({ ...newCycle, endDate: e.target.value });
                        if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: undefined });
                      }}
                      onBlur={handleBlur}
                      onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                      className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.endDate && formErrors.endDate ? "border-red-500" : "border-gray-300"}`}
                      min={newCycle.startDate || (new Date()).toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  {touched.endDate && formErrors.endDate && (
                    <p className="mt-1 text-xs text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.endDate}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={newCycle.status}
                    onChange={(e) =>
                      setNewCycle({ ...newCycle, status: e.target.value })
                    }
                    className="mt-1 block w-full border border-gray-300 rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                  >
                    <option value="draft">Draft</option>
                    <option value="launched">Launched</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowCreateModal(false);
                      onClose?.();
                    }}
                    className="btn-cancel"
                  >
                    Cancel
                  </Button>
                  <PermissionGate permission="performance" action="create">
                    <Button
                      type="submit"
                      variant="primary"
                      className="btn-create"
                    >
                      Create
                    </Button>
                  </PermissionGate>
                </div>
              </form>
            </div>
          ) : (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-[1px] flex items-center justify-center z-[99999] !mt-0 p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold">Create New Cycle</h2>
                  <Button
                    variant="ghost"
                    icon={<X className="h-5 w-5" />}
                    onClick={() => setShowCreateModal(false)}
                  />
                </div>
                <form onSubmit={handleCreateCycle} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={newCycle.name}
                      onChange={(e) => {
                        const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 100);
                        setNewCycle({ ...newCycle, name: value });
                        if (formErrors.name) setFormErrors({ ...formErrors, name: undefined });
                      }}
                      onBlur={handleBlur}
                      onKeyPress={handleTextKeyPress}
                      maxLength={100}
                      className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.name && formErrors.name ? "border-red-500" : "border-gray-300"}`}
                      required
                    />
                    <div className="flex justify-between items-center mt-1">
                      {touched.name && formErrors.name && (
                        <p className="text-xs text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.name}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {newCycle.name.length}/100 characters
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="description"
                      value={newCycle.description}
                      onChange={(e) => {
                        const value = e.target.value.replace(INVALID_CHARS_REGEX, '').slice(0, 500);
                        setNewCycle({ ...newCycle, description: value });
                        if (formErrors.description) setFormErrors({ ...formErrors, description: undefined });
                      }}
                      onBlur={handleBlur}
                      onKeyPress={handleTextKeyPress}
                      maxLength={500}
                      className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.description && formErrors.description ? "border-red-500" : "border-gray-300"}`}
                      required
                      rows={6}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {touched.description && formErrors.description && (
                        <p className="text-xs text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 ml-auto">
                        {newCycle.description.length}/500 characters
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="startDate"
                        value={newCycle.startDate}
                        onChange={(e) => {
                          setNewCycle({ ...newCycle, startDate: e.target.value });
                          if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: undefined });
                        }}
                        onBlur={handleBlur}
                        onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                        className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.startDate && formErrors.startDate ? "border-red-500" : "border-gray-300"}`}
                        min={(new Date()).toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    {touched.startDate && formErrors.startDate && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {formErrors.startDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        name="endDate"
                        value={newCycle.endDate}
                        onChange={(e) => {
                          setNewCycle({ ...newCycle, endDate: e.target.value });
                          if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: undefined });
                        }}
                        onBlur={handleBlur}
                        onClick={(e) => typeof e.target.showPicker === 'function' && e.target.showPicker()}
                        className={`mt-1 block w-full border rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand ${touched.endDate && formErrors.endDate ? "border-red-500" : "border-gray-300"}`}
                        min={newCycle.startDate || (new Date()).toISOString().split('T')[0]}
                        required
                      />
                    </div>
                    {touched.endDate && formErrors.endDate && (
                      <p className="mt-1 text-xs text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {formErrors.endDate}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      name="status"
                      value={newCycle.status}
                      onChange={(e) =>
                        setNewCycle({ ...newCycle, status: e.target.value })
                      }
                      className="mt-1 block w-full border border-gray-300 rounded-lg p-2 outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
                    >
                      <option value="draft">Draft</option>
                      <option value="launched">Launched</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button
                      variant="primary"
                      onClick={() => setShowCreateModal(false)}
                      className="btn-cancel"
                    >
                      Cancel
                    </Button>
                    <PermissionGate permission="performance" action="create">
                      <Button
                        type="submit"
                        variant="primary"
                        className="btn-create"
                        disabled={false}
                      >
                        Create
                      </Button>
                    </PermissionGate>
                  </div>
                </form>
              </div>
            </div>
          )
        )
      }

      {
        !isDrawer && (
          <>
            {/* Loading State */}
            {isLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading performance cycles...</p>
              </div>
            )}

            {/* Cycles List */}
            {!isLoading && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 capitalize">
                    <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                      <tr>
                        <th className="px-6 py-3 text-left w-10">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                            checked={filteredCycles.length > 0 && filteredCycles.every(c => selectedCyclesMap.has(c.id))}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                          onClick={() => handleSort('name')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Cycle Details</span>
                            {sortField === 'name' ? (
                              sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                            ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                          onClick={() => handleSort('year')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Period</span>
                            {sortField === 'year' ? (
                              sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                            ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                          onClick={() => handleSort('status')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Status</span>
                            {sortField === 'status' ? (
                              sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                            ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                          onClick={() => handleSort('dueDate')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Due Date</span>
                            {sortField === 'dueDate' ? (
                              sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                            ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                          </div>
                        </th>
                        <th
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none whitespace-nowrap"
                          onClick={() => handleSort('createdAt')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Created At</span>
                            {sortField === 'createdAt' ? (
                              sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-brand" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-brand" />
                            ) : <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />}
                          </div>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredCycles.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <FileText className="h-12 w-12 text-gray-400 mb-4" />
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No performance cycles found</h3>
                              {(searchTerm || filterStatus !== 'all' || filterYear !== 'all') && (
                                <p className="text-sm text-gray-500">Try adjusting your search or filter criteria.</p>
                              )}
                              {!searchTerm && filterStatus === 'all' && filterYear === 'all' && (
                                <p className="text-sm text-gray-500">Get started by creating your first performance cycle.</p>
                              )}
                            </div>
                          </td>
                        </tr>
                      ) : (
                        sortedCycles.map((cycle) => (
                          <tr key={cycle.id} className={`hover:bg-blue-50/30 ${selectedCyclesMap.has(cycle.id) ? 'bg-brand/5' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-brand focus:ring-brand h-4 w-4"
                                checked={selectedCyclesMap.has(cycle.id)}
                                onChange={() => handleSelectRow(cycle)}
                              />
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div
                                  className="text-sm font-medium text-gray-900"
                                  title={cycle.name}
                                >
                                  {cycle.name && cycle.name.length > 30
                                    ? `${cycle.name.slice(0, 30)}...`
                                    : cycle.name}
                                </div>
                                <div
                                  className="text-sm text-gray-500"
                                  title={cycle.description}
                                >
                                  {cycle.description && cycle.description.length > 30
                                    ? `${cycle.description.slice(0, 30)}...`
                                    : cycle.description}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {cycle.period}
                              </div>
                              <div className="text-xs text-gray-500">
                                Year: {cycle.year}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                  cycle.status
                                )}`}
                              >
                                {getStatusIcon(cycle.status)}
                                <span className="ml-1 capitalize">{cycle.status}</span>
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {cycle.dueDate ? formatDate(cycle.dueDate, { includeTime: false }) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {cycle.createdAt ? formatDate(cycle.createdAt, { includeTime: true }) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <PermissionGate permission="performance" action="read">
                                  <button
                                    onClick={() => handleViewCycle(cycle.id)}
                                    className="p-2 text-brand hover:text-brand-dark hover:bg-brand/5 rounded-lg transition-colors"
                                    title="View Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                <PermissionGate permission="performance" action="update">
                                  <button
                                    onClick={() => handleEditCycle(cycle.id)}
                                    className="p-2 text-brand hover:text-brand-dark hover:bg-brand/5 rounded-lg transition-colors"
                                    title="Edit"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </button>
                                </PermissionGate>
                                {(() => {
                                  const isDeletable = cycle.status === "draft" || cycle.status === "closed";
                                  return (
                                    <PermissionGate permission="performance" action="delete">
                                      <button
                                        onClick={isDeletable ? () => handleDeleteCycle(cycle.id) : undefined}
                                        disabled={!isDeletable}
                                        className={`p-2 rounded-lg transition-colors ${isDeletable
                                          ? "text-red-600 hover:text-red-800 hover:bg-red-50"
                                          : "text-gray-400 opacity-50 cursor-not-allowed"
                                          }`}
                                        title={
                                          isDeletable
                                            ? "Delete cycle"
                                            : "Cannot delete a cycle that is not in draft or closed status"
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </PermissionGate>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {paginationData && (
                  <PaginationControls
                    currentPage={page}
                    totalPages={paginationData.totalPages}
                    onPageChange={setPage}
                    limit={limit}
                    onLimitChange={setLimit}
                    totalItems={paginationData.totalCycles}
                    border={false}
                  />
                )}
              </div>
            )}
          </>
        )
      }

      {/* Cycle Stages Overview removed as requested */}

      {/* Delete Warning Popup */}
      <WarningPopup
        isOpen={showDeleteWarning}
        onClose={cancelDeleteCycle}
        onConfirm={confirmDeleteCycle}
        title={isBulkDelete ? "Delete Performance Cycles" : "Delete Performance Cycle"}
        message={isBulkDelete
          ? `Are you sure you want to delete ${selectedCyclesMap.size} selected performance cycle(s)? Only cycles in draft or closed status will be deleted. This action cannot be undone.`
          : `Are you sure you want to delete "${cycleToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* View Cycle Modal */}
      {
        showViewModal && selectedCycle && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
              <div className="flex justify-between items-start mb-4 gap-4">
                <div className="max-w-full">
                  <h2 className="text-xl font-semibold break-words capitalize">
                    {selectedCycle.name}
                  </h2>
                  {selectedCycle.description && (
                    <p className="text-gray-600 text-sm mt-1 whitespace-pre-wrap break-words capitalize">
                      {selectedCycle.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs uppercase text-gray-500 mb-1">Period</p>
                  <p className="text-gray-900 break-words whitespace-pre-wrap">
                    {getCyclePeriodDisplay(selectedCycle)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs uppercase text-gray-500 mb-1">Status</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedCycle.status)}`}>
                    {getStatusIcon(selectedCycle.status)}
                    <span className="ml-1 capitalize">{selectedCycle.status}</span>
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs uppercase text-gray-500 mb-1">Due Date</p>
                  <p className="text-gray-900">{selectedCycle.dueDate ? formatDate(selectedCycle.dueDate, { includeTime: false }) : 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs uppercase text-gray-500 mb-1">Year</p>
                  <p className="text-gray-900">{selectedCycle.year}</p>
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
                <Button
                  variant="primary"
                  icon={<Edit className="h-4 w-4" />}
                  onClick={() => {
                    setShowViewModal(false);
                    handleEditCycle(selectedCycle.id);
                  }}
                  className="bg-brand hover:bg-brand-dark text-white border-transparent"
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>

        )
      }



      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Performance Cycles"
        showDateRange
        scopedFormatExport={{
          onExport: handleCyclesScopedExport,
          formats: ['csv', 'xlsx', 'pdf'],
          showCurrentPage: true,
          showFiltered: true,
          showDateRange: true,
          showAll: true,
        }}
      />
      {showBulkUpload && (
        <BulkUploadModal
          isOpen={showBulkUpload}
          onClose={() => setShowBulkUpload(false)}
          onSuccess={() => refetch()}
          config={{
            title: "Bulk Upload Performance Cycles",
            subtitle: "Import multiple performance cycles using a CSV file",
            templateEndpoint: "/performance/cycles/bulk-upload/template",
            validateEndpoint: "/performance/cycles/bulk-upload/validate",
            uploadEndpoint: "/performance/cycles/bulk-upload",
            templateFilename: "performance_cycles_bulk_upload_template.csv",
            dataKey: "data",
            maxRecords: 100,
            instructions: [
              "Cycle Name is required and must be unique.",
              "Start Date and End Date are required (YYYY-MM-DD).",
              "End Date must be after Start Date.",
              "Description is optional.",
              "Status can be: draft, launched, closed (default: draft)."
            ]
          }}
        />
      )}
    </div>
  );
};

export default PerformanceCycles;
